import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  UserPlus, Trash2, Edit3, UploadCloud, AlertTriangle, CheckCircle2,
  User, FileText, X, Phone, Mail, MapPin, Hash, Calendar, FlaskConical,
  CreditCard as CardIcon, Eye, Users,
} from "lucide-react";

const DRIVER_DOC_CATEGORIES = ["CDL", "MVR", "DQ-File", "Drug-Test", "Medical-Card"];

function daysUntil(iso) {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function expiryTag(iso) {
  if (!iso) return <span className="text-slate-400 font-mono text-xs">—</span>;
  const d = daysUntil(iso);
  const short = iso.slice(0, 10);
  if (d < 0) return <span className="hs-tag hs-tag-danger">EXPIRED</span>;
  if (d <= 30) return <span className="hs-tag hs-tag-danger">{d}d · {short}</span>;
  if (d <= 60) return <span className="hs-tag hs-tag-warn">{d}d · {short}</span>;
  return <span className="hs-tag hs-tag-success">{short}</span>;
}

export default function Drivers({ adminClientId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const effectiveClientId = adminClientId || (isAdmin ? null : user?.id);
  const qs = adminClientId ? `?client_id=${adminClientId}` : "";
  const [drivers, setDrivers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, a] = await Promise.all([
        api.get(`/drivers${qs}`),
        api.get(`/drivers/alerts${qs}`),
      ]);
      setDrivers(d.data); setAlerts(a.data);
    } finally { setLoading(false); }
  }, [qs]);
  useEffect(() => { load(); }, [load]);

  const remove = async (d) => {
    if (!window.confirm(`Delete driver "${d.name}"?`)) return;
    await api.delete(`/drivers/${d.id}`);
    toast.success("Driver removed");
    load();
  };

  return (
    <div className="space-y-6" data-testid="drivers-page">
      {!adminClientId && (
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="hs-overline text-blue-700">FLEET · DRIVERS</div>
            <h1 className="font-display font-black text-4xl uppercase tracking-tighter mt-2">Drivers</h1>
            <p className="text-sm text-slate-500 mt-2 max-w-2xl">Maintain a profile per driver — CDL, MVR, DQ file, drug tests and medical cards.</p>
          </div>
          <button onClick={() => { setEditing(null); setShowModal(true); }} className="hs-btn-primary inline-flex items-center gap-2" data-testid="add-driver-button">
            <UserPlus size={14}/> ADD DRIVER
          </button>
        </div>
      )}
      {adminClientId && (
        <div className="flex items-center justify-between">
          <div className="hs-overline">DRIVERS ({drivers.length})</div>
          <button onClick={() => { setEditing(null); setShowModal(true); }} className="hs-btn-primary inline-flex items-center gap-2" data-testid="add-driver-button">
            <UserPlus size={14}/> ADD DRIVER
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat icon={Users} label="Active Drivers" value={drivers.filter(d => d.status === "active").length} />
        <Stat icon={AlertTriangle} label="CDL Expiring 60d" value={alerts.filter(a => a.kind === "CDL").length} accent={alerts.some(a => a.kind === "CDL") ? "#DC2626" : "#059669"} />
        <Stat icon={AlertTriangle} label="Medical Expiring 60d" value={alerts.filter(a => a.kind === "Medical Card" || a.kind === "Medical").length} accent={alerts.some(a => a.kind?.startsWith("Medical")) ? "#DC2626" : "#059669"} />
        <Stat icon={FlaskConical} label="Drug Test Due 60d" value={alerts.filter(a => a.kind === "Drug test").length} accent={alerts.some(a => a.kind === "Drug test") ? "#D97706" : "#059669"} />
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="hs-card p-5 border-amber-400" data-testid="driver-alerts-panel">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={14} className="text-amber-600" />
            <span className="hs-overline">UPCOMING EXPIRATIONS · 60 DAYS</span>
          </div>
          <div className="space-y-1">
            {alerts.slice(0, 6).map((a, i) => (
              <div key={i} className="flex items-center justify-between text-sm py-1">
                <span>{a.driver_name} — <span className="text-slate-500">{a.kind}</span></span>
                <span className={`font-mono text-xs ${a.severity === "expired" ? "text-red-600" : a.severity === "critical" ? "text-red-600" : "text-amber-600"}`}>
                  {a.days_left < 0 ? `${Math.abs(a.days_left)}d overdue` : `${a.days_left}d · ${a.due}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="hs-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-left">
            <tr>
              <Th>Driver</Th><Th>Status</Th><Th>CDL #</Th><Th>CDL Expires</Th>
              <Th>Medical Expires</Th><Th>Last Drug Test</Th><Th>Next Drug Test</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-6 hs-overline">LOADING…</td></tr>}
            {!loading && drivers.length === 0 && (
              <tr><td colSpan={8} className="p-12 text-center text-sm text-slate-500">No drivers on file. Add your first driver.</td></tr>
            )}
            {drivers.map((d) => (
              <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setDetailId(d.id)} data-testid={`driver-row-${d.id}`}>
                <td className="px-4 py-3">
                  <button onClick={(e) => { e.stopPropagation(); setDetailId(d.id); }} className="text-left hover:text-blue-700 font-medium" data-testid={`driver-open-${d.id}`}>
                    {d.name}
                  </button>
                  <div className="text-xs text-slate-500">{d.email || "—"}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`hs-tag ${d.status === "active" ? "hs-tag-success" : "hs-tag-warn"}`}>{(d.status || "active").toUpperCase()}</span>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{d.cdl_number || "—"}{d.cdl_class && ` · Class ${d.cdl_class}`}</td>
                <td className="px-4 py-3">{expiryTag(d.cdl_expires)}</td>
                <td className="px-4 py-3">{expiryTag(d.medical_card_expires)}</td>
                <td className="px-4 py-3 font-mono text-xs">{d.last_drug_test_date ? `${d.last_drug_test_date.slice(0, 10)} · ${(d.last_drug_test_result || "—").toUpperCase()}` : "—"}</td>
                <td className="px-4 py-3">{expiryTag(d.next_drug_test_date)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => setDetailId(d.id)} className="hs-btn-ghost p-2" title="View profile" data-testid={`driver-view-${d.id}`}><Eye size={13}/></button>
                  <button onClick={() => { setEditing(d); setShowModal(true); }} className="hs-btn-ghost p-2" title="Edit" data-testid={`driver-edit-${d.id}`}><Edit3 size={13}/></button>
                  <button onClick={() => remove(d)} className="hs-btn-ghost p-2 text-red-600" title="Delete" data-testid={`driver-delete-${d.id}`}><Trash2 size={13}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <DriverModal
          initial={editing}
          clientId={effectiveClientId}
          qs={qs}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSaved={async () => { setShowModal(false); setEditing(null); await load(); toast.success("Driver saved"); }}
        />
      )}
      {detailId && (
        <DriverDetail
          driverId={detailId}
          isAdmin={isAdmin}
          onClose={() => { setDetailId(null); load(); }}
        />
      )}
    </div>
  );
}

function Th({ children }) { return <th className="px-4 py-3 hs-overline">{children}</th>; }

function Stat({ icon: Icon, label, value, accent = "#1D4ED8" }) {
  return (
    <div className="hs-card p-4" data-testid={`driver-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="hs-overline">{label}</span>
        <Icon size={14} style={{ color: accent }} />
      </div>
      <div className="font-mono font-black text-2xl mt-2" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function DriverModal({ initial, clientId, qs, onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    name: "", status: "active", hire_date: "",
    cdl_number: "", cdl_class: "A", cdl_state: "", cdl_expires: "",
    medical_card_expires: "",
    phone: "", email: "",
    last_drug_test_date: "", last_drug_test_result: "", next_drug_test_date: "",
    notes: "",
    insurance_added: false,
    clearinghouse_query_done: false, clearinghouse_query_date: "",
    mvr_pulled: false, mvr_pulled_date: "",
    dq_file_complete: false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k) => () => setForm({ ...form, [k]: !form[k] });

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Driver name required"); return; }
    setBusy(true);
    try {
      // Coerce date-only fields to "YYYY-MM-DD" format (backend stores as string)
      const payload = { ...form };
      for (const k of ["hire_date", "cdl_expires", "medical_card_expires", "last_drug_test_date", "next_drug_test_date", "clearinghouse_query_date", "mvr_pulled_date"]) {
        if (payload[k]) payload[k] = payload[k].slice(0, 10);
      }
      if (initial?.id) await api.put(`/drivers/${initial.id}`, payload);
      else await api.post(`/drivers${qs}`, payload);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="hs-card p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="driver-modal">
        <div className="flex items-start justify-between">
          <div>
            <div className="hs-overline">{initial ? "EDIT" : "ADD"} DRIVER</div>
            <h3 className="font-display font-bold text-2xl mt-1">{initial?.name || "New Driver"}</h3>
          </div>
          <button onClick={onClose} className="hs-btn-ghost p-2"><X size={16}/></button>
        </div>

        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <Field label="Full name" required value={form.name} onChange={set("name")} testid="driver-name"/>
          <div>
            <label className="hs-overline">Status</label>
            <select className="hs-input mt-2" value={form.status} onChange={set("status")} data-testid="driver-status">
              <option value="active">Active</option>
              <option value="on_leave">On Leave</option>
              <option value="terminated">Terminated</option>
            </select>
          </div>
          <Field label="Email" type="email" value={form.email || ""} onChange={set("email")} testid="driver-email"/>
          <Field label="Phone" value={form.phone || ""} onChange={set("phone")} testid="driver-phone"/>
          <Field label="Hire Date" type="date" value={(form.hire_date || "").slice(0, 10)} onChange={set("hire_date")} testid="driver-hire"/>
          <div />
        </div>

        <div className="hs-overline mt-6 mb-2">CDL</div>
        <div className="grid sm:grid-cols-4 gap-3">
          <Field label="CDL Number" value={form.cdl_number || ""} onChange={set("cdl_number")} testid="driver-cdl"/>
          <div>
            <label className="hs-overline">Class</label>
            <select className="hs-input mt-2" value={form.cdl_class || ""} onChange={set("cdl_class")} data-testid="driver-cdl-class">
              <option value="">—</option>
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="C">C</option>
            </select>
          </div>
          <Field label="State" value={form.cdl_state || ""} onChange={set("cdl_state")} testid="driver-cdl-state"/>
          <Field label="Expires" type="date" value={(form.cdl_expires || "").slice(0, 10)} onChange={set("cdl_expires")} testid="driver-cdl-expires"/>
        </div>

        <div className="hs-overline mt-6 mb-2">MEDICAL & DRUG TESTING</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Medical Card Expires" type="date" value={(form.medical_card_expires || "").slice(0, 10)} onChange={set("medical_card_expires")} testid="driver-medical"/>
          <Field label="Last Drug Test" type="date" value={(form.last_drug_test_date || "").slice(0, 10)} onChange={set("last_drug_test_date")} testid="driver-drug-last"/>
          <div>
            <label className="hs-overline">Last Result</label>
            <select className="hs-input mt-2" value={form.last_drug_test_result || ""} onChange={set("last_drug_test_result")} data-testid="driver-drug-result">
              <option value="">—</option>
              <option value="pass">Pass</option>
              <option value="fail">Fail</option>
              <option value="refused">Refused</option>
            </select>
          </div>
          <Field label="Next Drug Test Due" type="date" value={(form.next_drug_test_date || "").slice(0, 10)} onChange={set("next_drug_test_date")} testid="driver-drug-next" className="sm:col-span-3"/>
        </div>

        <div className="mt-4">
          <label className="hs-overline">Notes</label>
          <textarea className="hs-input mt-2 min-h-[64px]" value={form.notes || ""} onChange={set("notes")} data-testid="driver-notes"/>
        </div>

        <div className="hs-overline mt-6 mb-2">COMPLIANCE CHECKLIST</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <CheckRow
            checked={!!form.insurance_added}
            onToggle={toggle("insurance_added")}
            label="Added to Insurance"
            testid="driver-chk-insurance"
          />
          <CheckRow
            checked={!!form.dq_file_complete}
            onToggle={toggle("dq_file_complete")}
            label="DQ-File Complete"
            testid="driver-chk-dq"
          />
          <CheckWithDate
            checked={!!form.clearinghouse_query_done}
            onToggle={toggle("clearinghouse_query_done")}
            dateValue={(form.clearinghouse_query_date || "").slice(0, 10)}
            onDateChange={set("clearinghouse_query_date")}
            label="D&A Clearinghouse Query"
            testid="driver-chk-clearinghouse"
          />
          <CheckWithDate
            checked={!!form.mvr_pulled}
            onToggle={toggle("mvr_pulled")}
            dateValue={(form.mvr_pulled_date || "").slice(0, 10)}
            onDateChange={set("mvr_pulled_date")}
            label="MVR Pulled"
            testid="driver-chk-mvr"
          />
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="hs-btn-secondary">CANCEL</button>
          <button onClick={save} disabled={busy} className="hs-btn-primary" data-testid="driver-save">{busy ? "SAVING…" : "SAVE DRIVER"}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, className = "", ...rest }) {
  return (
    <div className={className}>
      <label className="hs-overline">{label}</label>
      <input className="hs-input mt-2" data-testid={testid} {...rest}/>
    </div>
  );
}

function CheckRow({ checked, onToggle, label, testid }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testid}
      className={`flex items-center gap-3 p-3 border text-left transition ${
        checked ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white hover:bg-slate-50"
      }`}
    >
      <div className={`w-5 h-5 border-2 flex items-center justify-center flex-shrink-0 ${
        checked ? "border-emerald-600 bg-emerald-600" : "border-slate-300"
      }`}>
        {checked && <CheckCircle2 size={12} className="text-white" />}
      </div>
      <span className={`text-sm ${checked ? "font-bold text-emerald-900" : "text-slate-700"}`}>{label}</span>
    </button>
  );
}

function CheckWithDate({ checked, onToggle, dateValue, onDateChange, label, testid }) {
  return (
    <div className={`p-3 border ${checked ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`}>
      <button
        type="button"
        onClick={onToggle}
        data-testid={testid}
        className="flex items-center gap-3 text-left w-full"
      >
        <div className={`w-5 h-5 border-2 flex items-center justify-center flex-shrink-0 ${
          checked ? "border-emerald-600 bg-emerald-600" : "border-slate-300"
        }`}>
          {checked && <CheckCircle2 size={12} className="text-white" />}
        </div>
        <span className={`text-sm ${checked ? "font-bold text-emerald-900" : "text-slate-700"}`}>{label}</span>
      </button>
      {checked && (
        <input
          type="date"
          className="hs-input mt-2 text-xs"
          value={dateValue || ""}
          onChange={onDateChange}
          data-testid={`${testid}-date`}
        />
      )}
    </div>
  );
}

function DriverDetail({ driverId, isAdmin, onClose }) {
  const [data, setData] = useState(null);
  const [activeCat, setActiveCat] = useState("CDL");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/drivers/${driverId}`);
    setData(data);
  }, [driverId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", activeCat);
    fd.append("driver_id", driverId);
    setUploading(true);
    try {
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${activeCat} uploaded`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const view = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Cannot view document");
    }
  };

  const removeDoc = async (d) => {
    if (!window.confirm(`Delete ${d.filename}?`)) return;
    await api.delete(`/documents/${d.id}`);
    load();
  };

  if (!data) return null;
  const { driver, documents } = data;
  const docsByCat = DRIVER_DOC_CATEGORIES.reduce((acc, c) => {
    acc[c] = documents.filter((d) => d.category === c);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="hs-card w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="driver-detail-modal">
        <div className="p-6 border-b border-slate-200 flex items-start justify-between">
          <div>
            <div className="hs-overline">DRIVER PROFILE</div>
            <h2 className="font-display font-black text-3xl mt-1 flex items-center gap-3">
              <User size={24} className="text-blue-700"/> {driver.name}
            </h2>
            <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-500 font-mono">
              {driver.email && <span className="inline-flex items-center gap-1"><Mail size={12}/> {driver.email}</span>}
              {driver.phone && <span className="inline-flex items-center gap-1"><Phone size={12}/> {driver.phone}</span>}
              {driver.cdl_number && <span className="inline-flex items-center gap-1"><Hash size={12}/> CDL {driver.cdl_number} · Class {driver.cdl_class || "—"}</span>}
              {driver.cdl_state && <span className="inline-flex items-center gap-1"><MapPin size={12}/> {driver.cdl_state}</span>}
            </div>
          </div>
          <button onClick={onClose} className="hs-btn-ghost p-2"><X size={18}/></button>
        </div>

        {/* Expiry cards */}
        <div className="p-6 grid grid-cols-2 sm:grid-cols-4 gap-3 border-b border-slate-200">
          <MiniField icon={CardIcon} label="CDL Expires" value={driver.cdl_expires} />
          <MiniField icon={Calendar} label="Medical Card Expires" value={driver.medical_card_expires} />
          <MiniField icon={FlaskConical} label="Last Drug Test" value={driver.last_drug_test_date} trailing={driver.last_drug_test_result ? `(${driver.last_drug_test_result})` : ""} />
          <MiniField icon={FlaskConical} label="Next Drug Test" value={driver.next_drug_test_date} highlight />
        </div>

        {/* Document tabs */}
        <div className="px-6 pt-5">
          <div className="flex gap-2 flex-wrap">
            {DRIVER_DOC_CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setActiveCat(c)}
                data-testid={`driver-cat-${c}`}
                className={`px-3 py-2 text-xs border-b-2 transition-colors ${activeCat === c ? "border-blue-700 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-900"}`}
              >
                {c} ({docsByCat[c]?.length || 0})
              </button>
            ))}
          </div>

          <div className="mt-4 hs-file-drop p-5" data-testid="driver-upload-zone">
            <div className="flex items-center gap-4">
              <UploadCloud className="text-blue-700" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold">Upload {activeCat} document</div>
                <div className="text-xs text-slate-500 font-mono">PDF · PNG · JPG · WEBP · max 15MB</div>
              </div>
              <input ref={fileRef} type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} data-testid="driver-file-input" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} className="hs-btn-primary" data-testid="driver-upload-button">
                {uploading ? "UPLOADING…" : "BROWSE"}
              </button>
            </div>
          </div>

          <div className="mt-4 mb-6">
            {docsByCat[activeCat].length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">No {activeCat} documents yet.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {docsByCat[activeCat].map((d) => (
                  <div key={d.id} className="flex items-center justify-between gap-3 py-3" data-testid={`driver-doc-${d.id}`}>
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <FileText size={14} className="text-slate-400"/>
                      <span className="truncate text-sm">{d.filename}</span>
                      <span className={`hs-tag ${d.status === "approved" ? "hs-tag-success" : d.status === "rejected" ? "hs-tag-danger" : "hs-tag-warn"}`}>{(d.status || "pending").toUpperCase()}</span>
                    </div>
                    <span className="text-xs text-slate-500 font-mono">{(d.uploaded_at || "").slice(0, 10)}</span>
                    <button onClick={() => view(d)} className="hs-btn-ghost p-2" title="View"><Eye size={13}/></button>
                    <button onClick={() => removeDoc(d)} className="hs-btn-ghost p-2 text-red-600" title="Delete"><Trash2 size={13}/></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {driver.notes && (
          <div className="px-6 pb-6">
            <div className="hs-overline mb-1">NOTES</div>
            <div className="text-sm text-slate-700 whitespace-pre-wrap">{driver.notes}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function MiniField({ icon: Icon, label, value, trailing = "", highlight = false }) {
  const d = value ? daysUntil(value) : null;
  const tone = d == null ? "text-slate-500" : d < 0 ? "text-red-600" : d <= 30 ? "text-red-600" : d <= 60 ? "text-amber-600" : "text-emerald-600";
  return (
    <div className={`border border-slate-200 p-3 ${highlight ? "bg-blue-50/40" : ""}`}>
      <div className="flex items-center gap-2">
        <Icon size={12} className="text-blue-700"/>
        <span className="hs-overline text-[10px]">{label}</span>
      </div>
      <div className={`font-mono text-sm mt-1 ${tone}`}>
        {value ? `${value.slice(0, 10)}${trailing ? " " + trailing : ""}` : "—"}
        {d != null && <span className="ml-1 text-xs">({d < 0 ? `${Math.abs(d)}d over` : `${d}d`})</span>}
      </div>
    </div>
  );
}
